import { Package } from 'lucide-react'

import { getProductImageURL } from '@/lib/product-image'
import { toLocalYMD, vnCivilDate } from '@/lib/date-presets'
import { m } from '@/lib/paraglide/messages'

/**
 * Dòng "Tạo: dd/MM/yyyy HH:mm" cho option lô — giúp phân biệt lô tạo ngày nào.
 * Trả undefined khi thiếu/không hợp lệ (vd lô ảo STOCK gộp tồn — không có ngày tạo).
 */
export function createdAtLine(iso?: string | null): string | undefined {
    const t = createdAtText(iso)
    return t === undefined ? undefined : m.lot_option_created_at({ time: t })
}

/**
 * Ngày giờ tạo lô, KHÔNG kèm tiền tố "Tạo:" — dùng cho các cuốn sổ, nơi dòng
 * này là thứ người dùng nhìn để nhận ra lô nên phải to và gọn hết mức.
 * Luôn in đủ ngày; ngày gần thì kèm lời trong ngoặc: "13/09/2026 07:30 (hôm nay)".
 */
export function createdAtText(iso?: string | null): string | undefined {
    if (!iso) return undefined
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return undefined
    const hh = String(d.getHours()).padStart(2, '0')
    const mi = String(d.getMinutes()).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    return withRelativeDay(`${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`, d)
}

/**
 * Ngày lấy hương của lô (M-63) — xưởng nhận lô lấy hương theo ngày lấy hương
 * khai trên phiếu nhập, không theo ngày tạo lô. Cột đó là DATE (khai ngày,
 * không khai giờ) nên chỉ in ngày: "12/09/2026 (hôm qua)". Chỉ đọc phần
 * YYYY-MM-DD: API trả nửa đêm UTC, đổi múi giờ là lệch ngày.
 *
 * Lô cũ chưa có phiếu mang ngày lấy hương thì lùi về ngày giờ tạo lô.
 */
export function scentingDateText(
    scentingDate?: string | null,
    createdAt?: string | null,
): string | undefined {
    const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(scentingDate ?? '')
    if (!ymd) return createdAtText(createdAt)
    const [, y, mo, da] = ymd
    // Giữa trưa giờ VN: đổi sang ngày lịch VN kiểu gì cũng ra đúng ngày khai.
    const noon = new Date(`${y}-${mo}-${da}T12:00:00+07:00`)
    return withRelativeDay(`${da}/${mo}/${y}`, noon)
}

function withRelativeDay(text: string, d: Date): string {
    const rel = relativeDay(d)
    return rel ? `${text} (${rel})` : text
}

/**
 * "hôm nay" / "hôm qua" / "hôm kia" cho ba ngày gần nhất; xa hơn trả undefined
 * để chỗ gọi chỉ in ngày.
 *
 * Lô xưởng chọn trong ngày là chuyện thường xuyên nhất, mà đọc "13/09/2026" rồi
 * phải tự nhẩm xem có phải hôm nay không thì chậm — nhất là với người ghi sổ.
 *
 * So sánh theo NGÀY LỊCH VIỆT NAM (`vnCivilDate`) chứ không theo đồng hồ máy:
 * máy đặt sai múi giờ hay laptop mang đi công tác sẽ gọi lô tạo sáng nay là
 * "hôm qua". Cộng trừ ngày bằng `setDate` (thao tác trên thành phần lịch) thay
 * vì trừ 86.400.000 ms — trừ mili giây sẽ lệch một giờ ở máy có giờ mùa.
 */
function relativeDay(d: Date): string | undefined {
    const today = vnCivilDate()
    const target = toLocalYMD(vnCivilDate(d))

    if (target === toLocalYMD(today)) return m.lot_option_day_today()

    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)
    if (target === toLocalYMD(yesterday)) return m.lot_option_day_yesterday()

    const beforeYesterday = new Date(today)
    beforeYesterday.setDate(today.getDate() - 2)
    if (target === toLocalYMD(beforeYesterday)) {
        return m.lot_option_day_before_yesterday()
    }
    return undefined
}

/**
 * Bố cục 1 option lô trong Combobox chọn lô (dùng chung mọi loại lô).
 *
 * - Dòng 1: `title` — mô tả "là gì" (hộ dân / loại / tên vật tư / tên SP), đậm, truncate.
 * - Dòng 2: `code` (mono) + các `chips` ngắn (vị trí…) nối bằng `·` ở trái;
 *   `qty` (KL khả dụng, đậm) canh phải.
 * - Dòng 3 (tuỳ chọn): `thirdLine` — vd "NSX 01/2026 · HSD 12/2026" cho lô thành phẩm.
 * - `imageSlot=true`: chừa ô ảnh 40×40 bên trái (lô thành phẩm); `imageUrl` rỗng → icon.
 */
export interface LotOptionRowProps {
    title: string
    code: string
    qty: string
    chips?: Array<string | null | undefined>
    thirdLine?: string
    /** Dòng ngày giờ tạo (dùng `createdAtLine`) — phân biệt lô tạo ngày nào. */
    createdLine?: string
    imageSlot?: boolean
    imageUrl?: string | null
    /**
     * Kiểu dành cho các cuốn SỔ (#M-16): giấu mã lô và phóng to ngày giờ tạo.
     *
     * Người ghi sổ là bác lớn tuổi ở xưởng, mắt kém và không quan tâm mã lô —
     * họ nhận ra lô bằng loại chè và giờ tạo. Ở màn PHIẾU thì ngược lại, kế toán
     * cần mã lô để đối chiếu chứng từ, nên mặc định vẫn giữ nguyên như cũ.
     */
    ledgerStyle?: boolean
    /**
     * Nhãn CK của lô ("CK 25%", M-129) — đứng ngay cạnh tên, không co lại, để
     * dòng ngày giờ/số lượng dài cỡ nào cũng không cắt mất nó. Không truyền thì
     * không có nhãn.
     */
    discountLabel?: string
}

/** Nhãn CK lô dạng viên màu hổ phách — cùng tông với nhãn CK ở danh sách lô
 *  thành phẩm, cỡ chữ lớn hơn vì người ghi sổ đọc trên máy tính bảng. */
export function LotDiscountBadge({ label }: { label: string }) {
    return (
        <span className="inline-flex shrink-0 whitespace-nowrap rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-amber-800">
            {label}
        </span>
    )
}

export function LotOptionRow({
    title,
    code,
    qty,
    chips,
    thirdLine,
    createdLine,
    imageSlot,
    imageUrl,
    ledgerStyle,
    discountLabel,
}: LotOptionRowProps) {
    // Kiểu sổ: bỏ hẳn mã lô khỏi dòng phụ, chỉ còn các chip ngắn (vị trí…).
    const metaLeft = [...(ledgerStyle ? [] : [code]), ...(chips ?? [])]
        .filter(Boolean)
        .join(' · ')
    return (
        <div className="flex w-full items-center gap-3">
            {imageSlot &&
                (imageUrl ? (
                    <img
                        src={getProductImageURL(imageUrl)}
                        alt={title}
                        className="size-10 flex-shrink-0 rounded-md object-cover"
                    />
                ) : (
                    <div className="flex size-10 flex-shrink-0 items-center justify-center rounded-md bg-muted">
                        <Package className="size-5 text-muted-foreground" />
                    </div>
                ))}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <div
                        className={`min-w-0 flex-1 truncate font-medium ${
                            ledgerStyle ? 'text-base' : 'text-sm'
                        }`}
                    >
                        {title}
                    </div>
                    {discountLabel && (
                        <LotDiscountBadge label={discountLabel} />
                    )}
                </div>
                <div className="flex items-baseline gap-2">
                    {/* Kiểu sổ: ngày giờ tạo lên chính dòng này và to bằng cỡ
                        chữ thân bài, vì đó là thứ người ghi sổ nhìn để nhận lô. */}
                    <div
                        className={
                            ledgerStyle
                                ? 'min-w-0 flex-1 truncate text-sm font-semibold tabular-nums'
                                : 'min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground'
                        }
                    >
                        {ledgerStyle ? createdLine : metaLeft}
                    </div>
                    <div
                        className={`whitespace-nowrap font-semibold tabular-nums ${
                            ledgerStyle ? 'text-base' : 'text-sm'
                        }`}
                    >
                        {qty}
                    </div>
                </div>
                {ledgerStyle && metaLeft && (
                    <div className="truncate text-xs text-muted-foreground">
                        {metaLeft}
                    </div>
                )}
                {!ledgerStyle && thirdLine && (
                    <div className="truncate text-xs text-muted-foreground">
                        {thirdLine}
                    </div>
                )}
                {!ledgerStyle && createdLine && (
                    <div className="truncate text-xs text-muted-foreground">
                        {createdLine}
                    </div>
                )}
            </div>
        </div>
    )
}
